import { Option, ReadonlyArray, Runtime, Either } from "fp";
import * as ReactQuery from "react-query";
import { Identity } from "@equipmentshare/auth0-react";

import * as Data from "~/Data";
import { HoursOfService } from "~/HoursOfService";
import { Remote as GenericRemote, Remote } from "~/Remote";
import { Request } from "~/Request";
import { useSelector } from "~/State";
import { Time } from "~/Time";

import { Assets as Assets_ } from "./Assets";
import { Location as Location_, Locations as Locations_ } from "./Location";
import { Branch as Branch_ } from "./Branch";
import { Settings as Settings_ } from "./Settings";

export type Company = {
  readonly company: Data.Reference.Reference & GenericRemote<Company.Details>;
};

export namespace Company {
  export import Assets = Assets_;
  export import Location = Location_;
  export import Branch = Branch_;
  export import Locations = Locations_;
  export import Settings = Settings_;

  export const fromID = (id: string): Company => ({
    company: { id, ...GenericRemote.idle() },
  });

  export const fromIdentity = ({ companyId }: Identity): Option<Company> =>
    pipe(
      Option.fromNullable(companyId),
      Option.mapNullable<number, string>((id) => (id ? `${id}` : undefined)),
      Option.map(fromID)
    );

  export const fromToken = (): Option<Company> =>
    pipe(
      document.cookie.split(";"),
      ReadonlyArray.findFirst((cookie) => cookie.includes("session=")),
      Option.chain(
        flow(
          (cookie) => cookie.split("="),
          ([, encoded]) => Option.fromNullable(encoded)
        )
      ),
      Option.chain((cookie) => Option.tryCatch(() => JSON.parse(atob(cookie)))),
      Option.mapNullable((cookie) => cookie["companyId"] as string | undefined),
      Option.map(fromID)
    );

  export const use = (
    queryOptions?: ReactQuery.UseQueryOptions<
      Either.ErrorOr<Details>,
      Error,
      Either.ErrorOr<Details>
    >
  ): Company =>
    pipe(
      useSelector(({ companies: { current } }) => current.company.id),
      (id) => ({
        company: {
          id,
          ...Details.use(
            { company: { id, ...GenericRemote.loading() } },
            queryOptions
          ),
        },
      })
    );

  export type State = { readonly current: Company };
  export namespace State {
    export const initial = (company: Company): State => ({ current: company });
  }

  export type Details = Partial<Location & HoursOfService> & {
    readonly dot?: string;
    readonly time?: Time.Zone;
  };

  export namespace Details {
    export const use = (
      { company: { id } }: Company,
      queryOptions?: ReactQuery.UseQueryOptions<
        Either.ErrorOr<Details>,
        Error,
        Either.ErrorOr<Details>
      >
    ): Remote<Details> =>
      GenericRemote.Query.use(
        ["Company", id],
        request(fromID(id)),
        queryOptions
      );

    export namespace Invalidate {
      export const use = () => {
        const client = ReactQuery.useQueryClient();
        const id = useSelector(
          ({ companies: { current } }) => current.company.id
        );

        return () =>
          client.invalidateQueries(["Company", id], {
            refetchActive: true,
            refetchInactive: true,
          });
      };
    }

    /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/master/index.html?highlight=company#get--companies-(int-company_id)) */
    export const request = ({ company }: Company): Request.Fn<Details> =>
      flow(
        Request.fromConfig({ endpoint: `/companies/${company.id}` }),
        Request.Expect.status(200),
        Request.Expect.json(Details.Response.runtime()),
        Request.onSuccess(Details.Response.decode)
      );

    export namespace Update {
      export const use = () => GenericRemote.Mutation.use(request);

      /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/master/index.html?highlight=companies#patch--companies-(int-company_id)-elogs-info) */
      export const request = ({ company }: Company): Request.Fn => {
        const details = pipe(
          company,
          Remote.getOrElseW(() => undefined)
        );

        const config = {
          endpoint: `/companies/${company.id}/elogs-info`,
          ...(details && {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              home_office_location_id: details.location?.id,
              hours_of_service_type_id: details.hoursOfService?.type?.id,
            }),
          }),
        };

        return flow(Request.fromConfig(config), Request.Expect.status(200));
      };
    }

    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          dot_number: Runtime.nullable(Runtime.string()),
          timezone: Runtime.nullable(Runtime.string()),
          home_office_location: Runtime.nullable(Location.Response.runtime()),
          hours_of_service_type: Runtime.nullable(
            HoursOfService.Type.Response.runtime()
          ),
        });

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Details => ({
        dot: response.dot_number ?? undefined,
        time: Time.Zone.fromString(response.timezone ?? ""),
        ...(response.home_office_location &&
          Location.Response.decode(response.home_office_location)),
        ...(response.hours_of_service_type && {
          hoursOfService: HoursOfService.Type.Response.decode(
            response.hours_of_service_type
          ),
        }),
      });
    }
  }
  export namespace Fetch {
    export const refetch = ({ company: { id } }: Company) => {
      return Remote.Fetch.use(["Company", id], request(fromID(id)));
    };

    export const request = ({ company }: Company): Request.Fn =>
      flow(
        Request.fromConfig({ endpoint: `/companies/${company.id}` }),
        Request.Expect.status(200)
      );
  }
}
